import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { AccountService } from '../service/account.service';

const TUNER_GARAGE_PLANS = ['pro', 'ultra'];

/**
 * Fecha /tuner/garage/** para quem não tem plano Pro/Ultra ativo. A API já nega com 403
 * (GarageTunerPlanFilter, code "tuner.plan_required") em todos os endpoints da Oficina —
 * esta guarda é só UX: troca uma tela quebrada por um redirect com aviso.
 *
 * Aplicada em canActivate na rota que faz o loadChildren (app-routing.module.ts) E em
 * canActivateChild no módulo (tuner-garage.module.ts), para cobrir tanto o primeiro load
 * quanto a navegação direta a qualquer uma das rotas filhas (URL colada).
 *
 * status === 'active' faz o inadimplente cair aqui sem regra própria: para esta guarda,
 * cartão recusado e plano Basic são o mesmo caso. Falha ao buscar a conta também nega
 * (fail closed) — nunca libera por omissão.
 *
 * O gate enviado no state é 'tuner.garage_plan_required', DIFERENTE do code que a API
 * devolve ('tuner.plan_required' — não mexer nisso, é o mesmo código dos outros 403 de
 * plano da Oficina). É um gate só do front: navegar direto para /tuner/garage/cars não é a
 * mesma situação de um 403 batendo numa ação sobre dado de cliente, e o texto do modal
 * precisa falar da Oficina em si, não de projetos/arquivos de clientes.
 */
export const tunerGarageGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const accountService = inject(AccountService);

  const deny = async (): Promise<boolean> => {
    await router.navigate(['/tuner/dashboard'], { state: { gate: 'tuner.garage_plan_required' } });
    return false;
  };

  const userId = auth.getUserInformations();
  if (!userId) return deny();

  try {
    const account = await firstValueFrom(accountService.getAccount(userId));
    const plan = account.planInformation;
    const ok = plan?.status === 'active' && TUNER_GARAGE_PLANS.includes((plan?.name ?? '').toLowerCase());
    return ok ? true : deny();
  } catch {
    return deny();
  }
};
